
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import utilStyles from 'styles/utils.module.scss';
import styles from 'pages/events/index.module.scss';
import NavigationLayout from "components/layout/NavigationLayout";
import { useRouter } from "next/router";
import TitleBar from "components/layout/TitleBar";
import TopTabBar from "components/TopTabBar";
import { useEffect, useRef, useState } from "react";
import { Axios } from "api";
import { captureException, captureMessage } from "@sentry/nextjs";
import Loading from "components/Loading";
import classNames from "classnames";
import { formatDate, getDaysLeft, highlightMatchedText, isNightTime, showRewardToast, showToast } from "common/utils";
import Link from "next/link";
import { getEventRoute } from "common/const";
import InfiniteScroll from "react-infinite-scroll-component";
import { REWARD_KEY_EVENT_LIST_CHECK } from "pages/rewards";
import { SubscriptionButton } from "components/buttons/SubscriptionButton";
import useNotiSubscription from "hooks/useNotiSubscription";
import parse from "html-react-parser";
import useTranslation from "next-translate/useTranslation";
import { getLocalizationInfoInCSR } from "common/i18n";
export const KEY_TYPE_ALL = 'all';
export const KEY_TYPE_PROMOTION = 'promotion';
export const KEY_TYPE_GIFT = 'gift';
export const KEY_TYPE_REVIEWER = 'reviewer';
const OngoingEventListCardContent = ({ event, highlightKeyword }) => {
    const daysLeft = event.status === '진행중' && event.ends_at ? getDaysLeft(event.ends_at) : 0;
    const { t } = useTranslation('events-index');
    const eventName = highlightKeyword ? parse(highlightMatchedText(event.name, highlightKeyword)) : event.name;
    return (<>
            <div className={classNames(utilStyles.flexRow, utilStyles.justifyContentSpaceBetween, utilStyles.alignItemsCenter)}>
                        <span className={classNames(styles.cardStatus, event.status === '진행중' ? styles.cardStatusOpen : undefined)}>
                            {t(`Events.Status.${event.status}`)}
                        </span>
                <div className={styles.tagWrapper}>
                    {!!event.max_participant_count && event.type === 'gift' &&
            <div className={styles.cardMaxParticipantCount}>
                            <div>
                                {t('Events.recruitment', { count: event.max_participant_count })}
                            </div>
                        </div>}
                    {event.status === '진행중' && event.ends_at &&
            <div className={styles.cardDaysLeft}>
                            <div>
                                {daysLeft === 0 ?
                    t('Events.daysLeft.today') :
                    t('Events.daysLeft.days', { count: daysLeft })}
                            </div>
                        </div>}
                </div>
            </div>
            <div className={styles.nameAndDateRow}>
                <span className={classNames(styles.cardName, event.ends_at && styles.cardNameMaxWidth)}>{eventName}</span>
                {event.ends_at &&
            <div className={styles.cardEndDateWrapper}>
                        <span className={styles.cardEndDate}>~ {formatDate(event.ends_at).slice(2)}</span>
                    </div>}
            </div>
        </>);
};
const EVENT_OPEN_NOTI_KEY_PREFIX = 'event_open@';
const BeforeOpenEventListCardContent = ({ event, highlightKeyword }) => {
    const { t } = useTranslation('events-index');
    const notiKey = `${EVENT_OPEN_NOTI_KEY_PREFIX}${event.id}`;
    const { countryCode } = getLocalizationInfoInCSR();
    const handleSubscribe = () => {
        try {
            typeof window !== 'undefined' && Boolean(window.karrotPixel) && window.karrotPixel.track('Lead');
        }
        catch { }
        showToast(t('Events.notificationSuccess'), t('Events.notificationSuccessDetail'));
    };
    const handleUnsubscribe = () => {
        showToast(t('Events.notificationCancel'), null);
    };
    const { notiTypeAndKeyToIsSubscribedAndPushOn, toggleSubscription, overrideIsSubscribed } = useNotiSubscription({ [notiKey]: event.is_subscribed }, handleSubscribe, handleUnsubscribe);
    const isSubscribed = notiTypeAndKeyToIsSubscribedAndPushOn[notiKey];
    const handleClick = () => {
        toggleSubscription(notiKey);
    };
    const eventName = highlightKeyword ? parse(highlightMatchedText(event.name, highlightKeyword)) : event.name;
    return (<div className={styles.beforeOpenEventCardContent}>
            <div className={styles.beforeOpenEventCardContentLeft}>
                <span className={classNames(styles.cardStatus, styles.cardStatusOpen)}>
                    {t(`Events.Status.${event.status}`)}
                </span>
                <span className={classNames(styles.cardName, event.ends_at && styles.cardNameMaxWidth)}>
                    {eventName}
                </span>
            </div>
            {!(Boolean(countryCode) && countryCode !== 'KR') &&
            <SubscriptionButton isSubscribed={isSubscribed} onClick={handleClick}/>}
        </div>);
};
export const EventListCard = (props) => {
    const { event, highlightKeyword, marginTop } = props;
    const daysLeft = event.status === '진행중' && event.ends_at ? getDaysLeft(event.ends_at) : 0;
    const onClick = () => {
        if (props.onClick) {
            props.onClick();
        }
        else {
            typeof mixpanel !== 'undefined' && mixpanel.track('EventListPageEventCardClick', {
                eventId: event.id,
                eventName: event.name,
                eventStatus: event.status,
                daysLeft: daysLeft,
                eventType: event.type
            });
        }
    };
    const isBeforeOpen = event.status === '오픈예정';
    return (<Link href={event.banner_link || getEventRoute(event.id)}>
            <a className={styles.cardContainer} onClick={onClick} style={{ marginTop }}>
                {event.status !== '진행중' && event.status !== '오픈예정' &&
            <div className={styles.endCardOverLay}/>}
                <div className={styles.cardImageWrapper}>
                    <img src={event.banner_image_url} className={styles.cardImage} alt={event.name}/>
                </div>
                <div className={classNames(styles.cardInfoSection, isBeforeOpen && styles.beforeOpenCardInfoSection)}>
                    {isBeforeOpen ?
            <BeforeOpenEventListCardContent event={event} highlightKeyword={highlightKeyword}/> :
            <OngoingEventListCardContent event={event} highlightKeyword={highlightKeyword}/>}
                </div>
            </a>
        </Link>);
};
const PAGE_SIZE = 20;
const Events = () => {
    const { t } = useTranslation('events-index');
    const router = useRouter();
    const cursorRef = useRef(null);
    const defaultEventData = {
        events: []
    };
    const [isLoading, setIsLoading] = useState(true);
    const [eventData, setEventData] = useState(defaultEventData);
    const [error, setError] = useState(false);
    useEffect(() => {
        if (!router.isReady) {
            return;
        }
        if (!router.query.type) {
            router.replace({ query: Object.assign({}, router.query, { type: KEY_TYPE_ALL }) });
        }
    }, [router]);
    const fetchEvents = async () => {
        setIsLoading(true);
        try {
            const res = await Axios.get('v1/events/', { params: {
                    type: router.query.type === KEY_TYPE_ALL ? '' : router.query.type,
                    cursor: cursorRef.current,
                    size: PAGE_SIZE
                } });
            if (res.status < 400) {
                cursorRef.current = res.data.cursor;
                setEventData(oldVal => {
                    const newVal = JSON.parse(JSON.stringify(oldVal));
                    return {
                        events: [...newVal.events, ...res.data.event_list]
                    };
                });
            }
            else {
                captureMessage(JSON.stringify(res.data));
                setError(true);
            }
        }
        catch (e) {
            captureException(e);
            setError(true);
        }
        finally {
            setIsLoading(false);
        }
    };
    useEffect(async () => {
        if (!router.query.type)
            return;
        window.scrollTo(0, 0);
        cursorRef.current = null;
        setEventData(defaultEventData);
        await fetchEvents();
    }, [router.query?.type]);
    const completeReward = async () => {
        try {
            const res = await Axios.post(`/v1/rewards/`, {
                reward_key: REWARD_KEY_EVENT_LIST_CHECK
            });
            if (res.status < 400) {
                showRewardToast(router, res.data.given_reward.key, res.data.given_reward.point);
            }
            else {
                captureMessage(JSON.stringify(res.data));
            }
        }
        catch (e) {
            captureException(e);
        }
    };
    useEffect(() => {
        setTimeout(() => {
            completeReward();
        }, 2000);
    }, []);
    const onRegisterCoupon = () => {
        typeof mixpanel !== 'undefined' && mixpanel.track('EventListPageRegisterCouponClick');
    };
    const onTabClick = (tab, tabIdx) => {
        typeof mixpanel !== 'undefined' && mixpanel.track('EventListPageTabClick', {
            tab,
            tabIdx
        });
        router.replace({ query: Object.assign({}, router.query, { type: tab }) });
    };
    const onClose = () => {
        typeof mixpanel !== 'undefined' && mixpanel.track('EventListPageCloseClick');
        router.back();
    };
    return (<div className={utilStyles.pageContainer}>
            <div className={classNames(utilStyles.sidePadding, utilStyles.topSticky, utilStyles.whiteBackground)}>
                <TitleBar title={t('Events.title')} isBack close={onClose}/>
            </div>
            <div>
                <TopTabBar height={42} activeRoute={router.query.type} tabs={[
            {
                route: KEY_TYPE_ALL, title: t('Events.tabs.all'),
                onClick: () => {
                    onTabClick(KEY_TYPE_ALL, 0);
                }
            },
            {
                route: KEY_TYPE_PROMOTION, title: t('Events.tabs.promotion'),
                onClick: () => {
                    onTabClick(KEY_TYPE_PROMOTION, 1);
                }
            },
            {
                route: KEY_TYPE_REVIEWER, title: t('Events.tabs.reviewer'),
                onClick: () => {
                    onTabClick(KEY_TYPE_REVIEWER, 2);
                }
            },
            {
                route: KEY_TYPE_GIFT, title: t('Events.tabs.gift'),
                onClick: () => {
                    onTabClick(KEY_TYPE_GIFT, 3);
                }
            },
        ]}/>
                <div className={styles.eventListContainer}>
                    {!isLoading && eventData.events.length === 0 ?
            (<div className={styles.cardError}>
                                    {error
                    ?
                        t('Events.error')
                    :
                        t('Events.noEvents')}
                                </div>)
            :
                (isLoading && eventData.events.length === 0 ?
                    <Loading style={{ marginTop: 200, marginBottom: 150 }}/>
                    :
                        <InfiniteScroll scrollThreshold={'80%'} hasMore={!!cursorRef.current} dataLength={eventData.events.length} next={fetchEvents} endMessage={undefined} loader={<div>
                                                <Loading style={{ marginTop: 20, marginBottom: 20 }}/>
                                            </div>} style={{ overflow: 'hidden' }}>
                                        {eventData.events.map(e => <EventListCard key={e.id} event={e}/>)}
                                    </InfiniteScroll>)}
                </div>
            </div>
        </div>);
};
Events.getLayout = NavigationLayout;
export default Events;

    async function __Next_Translate__getStaticProps__1966fcd4f4f__(ctx) {
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/events/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1966fcd4f4f__ as getStaticProps }
  